import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Heading,
  Text,
  Stack,
  VStack,
  Flex,
  Button,
  Icon,
  Spacer,
  Container,
  HStack,
  StackSeparator,
  Badge,
  Tabs,
} from '@chakra-ui/react';
import { NavLink, useParams, useLoaderData, useNavigate } from 'react-router-dom';
import { Field, StaticField } from '@/lib/types';
import { PlusIcon } from '@heroicons/react/24/outline';
import QuickSearchForm from '@/components/ui/quick-search-form';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import CreateFieldModal from './create-field-modal';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import { FIELD_TYPES, STATIC_FIELDS } from '@/lib/constants';

interface Props {
  field: Field;
  isBase?: boolean;
}

function getFieldName(fieldType: number) {
  return FIELD_TYPES.find((field) => field.id === fieldType)?.name;
}

const Item = ({ field, isBase = false }: Props) => {
  return (
    <Stack direction={'row'} _hover={{ cursor: isBase ? 'not-allowed' : 'pointer' }}>
      <VStack align={'start'} gap={0}>
        <Text color={isBase ? 'gray.500' : 'gray.700'} fontSize={'md'} fontWeight={'medium'}>
          {field.name}
          <Badge ml={3} variant='surface' size='xs' color={isBase ? 'gray.400' : 'gray.700'}>
            {getFieldName(field.field_type)}
          </Badge>
        </Text>
      </VStack>
    </Stack>
  );
};

export default function Fields() {
  const isAdmin = useIsInAdminGroup();
  const isCancelled = useIsCancelled();
  const fields = useLoaderData() as Field[];
  const [filteredFields, setFilteredFields] = useState<Field[]>([]);
  const [searchResults, setSearchResults] = useState<Field[]>([]);
  const [tabIndex, switchTab] = useState('custom-field');
  const createFieldModal = useModalToggle();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const isStaticProperty = useCallback(
    (field: Field) => {
      return (
        STATIC_FIELDS.includes(field.unique_name as StaticField) ||
        (workspaceId && field.workspace_id !== parseInt(workspaceId, 10))
      );
    },
    [workspaceId],
  );

  useEffect(() => {
    const filtered = fields.filter((field) => {
      if (tabIndex === 'base-field') {
        return isStaticProperty(field);
      } else {
        return !isStaticProperty(field);
      }
    });
    setFilteredFields(filtered);
    setSearchResults(filtered);
  }, [tabIndex, fields, isStaticProperty]);

  const handleSearch = (search: string) => {
    const filtered = filteredFields.filter((field) => {
      return field.name.toLowerCase().includes(search.toLowerCase());
    });
    setSearchResults(filtered);
  };

  const handleSwitchTab = (tab: string) => {
    switchTab(tab);
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'4xl'}>
            <Flex alignItems={'center'} mb={4}>
              <Heading size='3xl'>Fields</Heading>
              <Spacer />
              {isAdmin && !isCancelled && (
                <Button colorPalette='brand' onClick={createFieldModal.open}>
                  <Icon as={PlusIcon} w={4} h={4} />
                  Create Field
                </Button>
              )}
            </Flex>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'4xl'}>
            <Tabs.Root
              value={tabIndex}
              variant='line'
              size='lg'
              mb={4}
              onValueChange={(e) => handleSwitchTab(e.value)}
            >
              <Tabs.List>
                <Tabs.Trigger value='custom-field' asChild>
                  <Button colorPalette='brand' variant='ghost'>
                    Custom field
                  </Button>
                </Tabs.Trigger>
                <Tabs.Trigger value='base-field' asChild>
                  <Button colorPalette='brand' variant='ghost'>
                    Base field
                  </Button>
                </Tabs.Trigger>
              </Tabs.List>
            </Tabs.Root>
            <HStack mb={8}>
              <QuickSearchForm handleSearch={handleSearch} />
            </HStack>
            <Stack gap={3} separator={<StackSeparator />}>
              {searchResults.map((field) =>
                isStaticProperty(field) ? (
                  <Item key={`${field.id}_${field.unique_name}`} field={field} isBase={true} />
                ) : (
                  <NavLink
                    key={`${field.id}_${field.unique_name}`}
                    to={`/${workspaceId}/fields/${field.id}/edit`}
                  >
                    <Item field={field} />
                  </NavLink>
                ),
              )}
            </Stack>
          </Container>

          <CreateFieldModal {...createFieldModal.props} onSuccess={() => navigate(0)} />
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
